import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MantineReactTable,
  useMantineReactTable,
  MRT_ShowHideColumnsButton,
  MRT_GlobalFilterTextInput,
} from 'mantine-react-table'
import { ActionIcon as MantineActionButton } from '@mantine/core'
import { PenIcon, Trash } from 'lucide-react'
import { getLocalization } from '@/widgets/LanguageSelect/lib/consts'
import { useMessageTemplatesTable } from '../../lib/use-message-templates-table'
import { TemplateListItem } from '@/shared/api/topli/TopliApi'

type MessageTemplatesTableProps = {
  addButtonSlot: ReactNode
  onOpenEditModal: (row: TemplateListItem) => void
  onOpenDeleteModal: (row: TemplateListItem) => void
}

export const MessageTemplatesTable: FC<MessageTemplatesTableProps> = props => {
  const { addButtonSlot, onOpenEditModal, onOpenDeleteModal } = props
  const {
    pagination,
    columns,
    fetchedMessageTemplates,
    totalRowCount,
    setPagination,
    isLoadingMessageTemplates,
    isErrorMessageTemplates,
    isFetchingMessageTemplates,
    handleSearchChange,
  } = useMessageTemplatesTable()
  const { i18n } = useTranslation()
  const localization = getLocalization(i18n.language)

  const table = useMantineReactTable({
    columns,
    data: fetchedMessageTemplates,
    enableStickyHeader: false,
    enableEditing: true,
    manualPagination: true,
    enableColumnActions: false,
    manualSorting: true,
    onPaginationChange: setPagination,
    rowCount: totalRowCount,
    positionActionsColumn: 'first',
    localization,
    onGlobalFilterChange: handleSearchChange,
    enableGlobalFilter: true,
    renderRowActions: ({ row }) => (
      <div className='flex flex-row space-x-1 pr-8'>
        <MantineActionButton
          onClick={() => onOpenEditModal(row.original)}
          className='text-blue bg-blue/10 hover:bg-blue hover:text-white rounded-full transition-all'
          variant='light'
        >
          <PenIcon size={16} />
        </MantineActionButton>
        <MantineActionButton
          onClick={() => onOpenDeleteModal(row.original)}
          className='text-red bg-red/10 hover:bg-red hover:text-white rounded-full transition-all'
          variant='light'
        >
          <Trash size={16} />
        </MantineActionButton>
      </div>
    ),
    renderTopToolbar: ({ table }) => {
      return (
        <div className='flex flex-row items-center justify-between mb-2'>
          <MRT_GlobalFilterTextInput table={table} />
          <div className='flex flex-row items-center space-x-2'>
            <MRT_ShowHideColumnsButton className='h-[2.5rem] w-[2.5rem] rounded-md' table={table} />
            {addButtonSlot}
          </div>
        </div>
      )
    },
    mantineToolbarAlertBannerProps: isErrorMessageTemplates
      ? {
          color: 'red',
          children: 'Error loading data',
        }
      : undefined,
    mantinePaperProps: {
      shadow: 'none',
      withBorder: false,
    },
    mantineTableProps: {
      sx: {
        tableLayout: 'auto',
      },
    },
    mantineTableBodyProps: {
      sx: {
        '&: td': {
          minWidth: 90,
        },
        '& tr:hover': {
          backgroundColor: '#FFFFFF',
        },
      },
    },
    mantineTableBodyRowProps: {
      className: 'hover:bg-[#F4FAFD]/10',
    },
    state: {
      isLoading: isLoadingMessageTemplates,
      pagination,
      showGlobalFilter: true,
      showAlertBanner: isErrorMessageTemplates,
      showProgressBars: isFetchingMessageTemplates,
    },
  })

  return <MantineReactTable table={table} />
}
